import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, Renderer2, ViewChild } from '@angular/core';
import { ControlContainer, FormGroup, FormGroupDirective } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { GetTableSelector, Select } from '@interfaces';

@Component({
  selector: 'app-custom-select',
  templateUrl: './custom-select.component.html',
  styleUrls: ['./custom-select.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }]
})
export class CustomSelectComponent implements OnInit, AfterViewInit {
  private destroyed$: Subject<boolean> = new Subject<boolean>();
  private emptyOption = [{ value: '', label: '' }];
  private allOptions = [];
  private auxSelected: string[] = [];
  private options = [];
  private selectedOption = [{ value: '', label: '' }];
  public childForm: FormGroup;
  public isLoading: boolean;
  public open = false;
  public optionsFiltered = [{ value: '', label: '' }];
  public inputValue = '';
  public panelClass = 'custom-select';
  public whiteSpaceRegex = / /g;
  public search = false;

  @Input() controlName: string;
  @Input() label: string;
  @Input() set values(values: Select[]) {
    if (values) {
      this.options = values;
      this.optionsFiltered = values;
    }
    if (this.minLength) {
      this.isLoading = false;
    }
    if (this.selectAllOption && !this.searcher?.nativeElement?.value) {
      this.allOptions = values;
    }
  }
  @Input() defaultValue?: string | string[];
  @Input() required = false;
  @Input() class?: string;
  @Input() searchBar = false;
  @Input() multiple = false;
  @Input() disabled = false;
  @Input() numeric = false;
  @Input() minLength: number;
  @Input() maxLength = 100;
  @Input() showMore = false;
  @Input() selectAllOption = false;
  @Input() preload = false;
  @Input() requiredLabel = false;

  @Output() showMoreChange: EventEmitter<boolean> = new EventEmitter();
  @Output() onChange: EventEmitter<any> = new EventEmitter();
  @Output() getData: EventEmitter<GetTableSelector> = new EventEmitter();


  @ViewChild(MatSelect) matSelect: MatSelect;
  @ViewChild('searcher') searcher: ElementRef;
  @ViewChild('options') optionStyle: ElementRef;

  constructor(
    private parentF: FormGroupDirective,
    private renderer: Renderer2
  ) { }

  ngOnInit(): void {
    this.childForm = this.parentF.form;
    this.panelClass += this.searchBar ? ' custom-select-has-search ' : '';
    this.panelClass += this.multiple ? ' custom-select-multiple' : '';
    if (this.minLength && !this.childForm.get(this.controlName).value) {
      this.optionsFiltered = this.emptyOption;
    }
  }

  ngAfterViewInit(): void {
    this.matSelect.openedChange.pipe(takeUntil(this.destroyed$)).subscribe(opened => {
      if (opened) {
        this.open = true;
        if (this.searcher) {
          this.searcher.nativeElement.focus();
        }
      } else {
        this.open = false;
        this.showMoreChange.emit(false);
        if (this.searchBar) {
          if (this.minLength) {
            if (this.childForm.get(this.controlName).value) {
              this.renderer.removeClass(this.optionStyle.nativeElement, 'hide');
              this.optionsFiltered = this.selectedOption;
            } else {
              this.renderer.addClass(this.optionStyle.nativeElement, 'hide');
              this.optionsFiltered = this.emptyOption;
            }
          } else {
            this.renderer.removeClass(this.optionStyle.nativeElement, 'hide');
            this.optionsFiltered = this.options;
          }
        }
      }
      if (this.searcher) {
        this.searcher.nativeElement.value = '';
        this.search = false;
      }
    });
  }

  public onSelect(ev): void {
    if (ev && this.childForm && this.controlName && !this.selectAllOption) {
      this.onChange.emit(ev);
      this.childForm.get(this.controlName).patchValue(ev);
      this.selectedOption = [this.options.find(value => value.value === ev)];
    }
  }

  public handleSearch(): void {
    if (this.minLength) {
      if (this.searcher.nativeElement.value.trim().length >= this.minLength) {
        this.isLoading = true;
        this.search = true;
        this.auxSelected = this.defaultValue as string[] || [];
        this.getData.emit({ filter: this.searcher.nativeElement.value.trim(), more: false });
      }
    } else {
      const aux = this.options.filter(res =>
        res.label.toLowerCase().substring(
          0, this.searcher.nativeElement.value.length
        ) === this.searcher.nativeElement.value.toLowerCase()
      );
      this.optionsFiltered = aux.length > 0 ? aux : [];
      if (this.multiple && this.selectAllOption && this.searcher.nativeElement?.value?.length > 0) {
        this.auxSelected = this.defaultValue as string[] || [];
      }
    }
  }

  public handleChange(event): void {
    this.inputValue = event.target.value;
    if (this.searcher.nativeElement?.value?.length === 0 && this.minLength && this.preload) {
      this.isLoading = true;
      this.search = false;
      this.getData.emit({ filter: null, more: false });
    }
  }

  public handleMore(): void {
    this.isLoading = true;
    this.getData.emit({ filter: this.searcher.nativeElement.value, more: true });
  }

  public toogleOpen(open: boolean): void {
    if (open && this.preload) {
      this.isLoading = true;
      this.getData.emit({ more: false });
    } else if (!open && !this.options.length && this.preload && this.showMore) {
      this.getData.emit({ more: false });
      this.parentF.form.get(this.controlName).patchValue(null);
    }
    if (this.optionsFiltered === this.emptyOption) {
      this.renderer.addClass(this.optionStyle.nativeElement, 'hide');
    } else {
      this.renderer.removeClass(this.optionStyle.nativeElement, 'hide');
    }
    this.inputValue = !open ? '' : this.inputValue;
  }

  public selectAll(): void {
    if (this.defaultValue[0] === 'allOptions') {
      this.defaultValue = ['allOptions', ...this.optionsFiltered.map(res => res?.value)];
      this.selectedOption = [...this.optionsFiltered];
    } else {
      this.defaultValue = [];
      this.selectedOption = this.emptyOption;
    }
    this.childForm.get(this.controlName).patchValue(this.defaultValue);
    this.onChange.emit(this.defaultValue);
  }

  public selectOption(value: string): void {
    // NOT DELETE: THE SPREAD OPERATOR IS USE FOR UPDATE DATA AND DONT BIND WHEN ASSIGN ONE VARIABLE TO TO OTHER
    if (this.multiple && this.selectAllOption) {
      if (this.searcher?.nativeElement?.value?.length > 0) {
        const index = this.auxSelected.indexOf(value);
        if (index !== -1) {
          this.auxSelected.splice(index, 1);
          this.defaultValue = [...this.auxSelected];
        } else {
          this.defaultValue = [...this.auxSelected, value];
        }
        this.auxSelected = this.defaultValue;
      }
      if (this.defaultValue[0] === 'allOptions' && (this.defaultValue.length - 1) !== this.allOptions.length) {
        this.defaultValue = this.defaultValue.slice(1);
      }
      if (this.defaultValue.length === this.allOptions.length) {
        this.selectedOption = [...this.optionsFiltered];
        this.defaultValue = ['allOptions', ...this.defaultValue];
      } else {
        const index2 = this.selectedOption.findIndex(val => {
          return val.value === value;
        });
        if (index2 !== -1) {
          this.selectedOption.splice(index2, 1);
        } else {
          this.selectedOption.push(this.optionsFiltered.find(val => val.value === value));
        }
      }
      this.childForm.get(this.controlName).patchValue(this.defaultValue);
      this.onChange.emit(this.defaultValue);
    }
  }
}
